import { connect } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import { useListSearch } from "../../../common/hooks";
import TooltipButton from "../../../common/tooltipButton";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import { useIntl } from "react-intl";
import List from "../../../common/list";
import { TextInput } from "../../../common/fields";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import {
  listRequest,
  setQuery,
} from "../../../../store/slices/learningObjectsSlice";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import { useNavigate } from "react-router-dom";

export const LearningObjectList = ({
  action,
  labels,
  list,
  listRequest,
  query,
  requesting,
  setQuery,
  user,
  totalCount,
  enterprise,
  token,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [previewUrl, setPreviewUrl] = useState(null);
  // The row menu is drawn again only when its row changes (PrimeReact
  // memoises body cells by row data), so Preview reads the session through a
  // ref: the access token the row was drawn with may have been refreshed
  // since, and an HTML preview carrying it would be refused.
  const session = useRef({ token, enterprise });
  session.current = { token, enterprise };
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [createdByNamePattern, setCreatedByNamePattern] = useListSearch(
    query?.createdBy,
    (value) => setQuery({ ...query, createdBy: value, pageNo: 1 })
  );
  const [namePattern, setNamePattern] = useListSearch(
    query?.namePattern,
    (value) => setQuery({ ...query, namePattern: value, pageNo: 1 })
  );

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.CreateScope
  );

  const fetchList = useCallback(() => {
    listRequest({
      ...query,
      sortColumn: query.sortColumn || "name",
    });
  }, [query, listRequest]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onSelectionChange = (selectedRecords) => {
    setSelectedRecords(selectedRecords?.value || []);
  };

  const handlePageChange = (pageData) => {
    if (
      query.pageSize !== pageData.rows ||
      query.pageNo !== pageData.page + 1
    ) {
      setQuery({
        ...query,
        pageNo: pageData.page + 1,
        pageSize: pageData.rows,
      });
    }
  };

  const handleOnSortChange = (sortData) => {
    setQuery({
      ...query,
      pageNo: 1,
      sortColumn: sortData.sortField,
      sortBy: sortData.sortOrder === 1 ? "asc" : "desc",
    });
  };

  const handleAddLearningObject = () => {
    navigate(RouteConstants.APP_ROUTE_CONTENTS_LEARNING_OBJECTS_ADD);
  };

  return (
    <div className="pb-8 md: pb-0">
      <div
        className="edexlms-lo-preview"
        width="100%"
        height="100%"
        style={{ display: previewUrl ? "block" : "none" }}
      >
        {(previewUrl && (
          <>
            <div
              className="edexlms-lo-preview-close"
              onClick={() => setPreviewUrl(null)}
            >
              <i className="close pi pi-times" style={{ fontSize: "1.5rem" }} />
            </div>
            <iframe
              src={`${new URL(
                previewUrl,
                process.env.REACT_APP_URL
              ).toString()}`}
              title="preview"
            />
          </>
        )) ||
          null}
      </div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "learning_objects" })}
          </div>
          <div className="edexlms-page-actions">
            <TooltipButton
              icon="pi pi-plus"
              onClick={handleAddLearningObject}
              size="small"
              label={intl.formatMessage({ id: "add_learning_object" })}
              type="button"
              disabled={!canAdd}
              tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
              tooltip={
                canAdd ? "" : intl.formatMessage({ id: "no_permission" })
              }
            />
          </div>
        </div>
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({
                id: "search_learning_objects",
              })}
              input={{
                name: "search",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({
                id: "search_created_by",
              })}
              input={{
                name: "search",
                value: createdByNamePattern,
                onChange: (e) => setCreatedByNamePattern(e.target.value),
              }}
            />
          </div>
          <List
            loading={action === "list" && requesting}
            onSelectionChange={onSelectionChange}
            handlePageChange={handlePageChange}
            handleOnSortChange={handleOnSortChange}
            data={list || []}
            selectedRecords={selectedRecords}
            pageQuery={query}
            selectionMode=""
            totalRecords={totalCount}
            handleRowClick={(data) =>
              navigate(
                `${RouteConstants.APP_ROUTE_CONTENTS_LEARNING_OBJECTS_EDIT.replace(
                  ":id",
                  data.data.id
                )}`
              )
            }
            columns={[
              {
                field: "name",
                header: "name",
                sortable: true,
                body: ListCellRenders.NameAndLogo("name", "bannerImage"),
              },
              {
                field: "type",
                header: "type",
                sortable: false,
                body: ListCellRenders.TypeAndContentType,
              },
              {
                field: "max_time_in_seconds",
                header: "max_time_in_seconds",
                sortable: false,
                body: (data) => {
                  return (data?.maxTimeInSecsAllowed / 60).toFixed(0) || "-";
                },
              },
              // {
              //   field: "tags",
              //   header: "tags",
              //   sortable: false,
              //   body: ListCellRenders.Tags,
              // },
              // {
              //   field: "isCertified",
              //   header: "isCertified",
              //   sortable: false,
              //   body: ListCellRenders.BooleanText("isCertified"),
              // },
              // Reward trigger column removed - using simple reward/points fields instead
              {
                field: "rewardId",
                header: "reward",
                sortable: false,
                body: (data) => {
                  return data?.reward?.name || "-";
                },
              },
              {
                field: "pointsOnCompletion",
                header: "point_on_completion",
                sortable: false,
                body: (data) => {
                  return data?.pointsOnCompletion || 0;
                },
              },
              {
                field: "createdByUser",
                header: "created_by",
                sortable: false,
                body: ListCellRenders.CreatedBy,
              },
              {
                field: "createdAt",
                header: "created_at",
                sortable: true,
                body: ListCellRenders.DateTime("createdAt"),
              },
              {
                field: "updatedAt",
                header: "updated_at",
                sortable: true,
                body: ListCellRenders.DateTime("updatedAt"),
              },
              {
                field: "actions",
                headerIcon: "pi-cog",
                align: "center",
                body: ListCellRenders.OtherActions([
                  {
                    label: "preview",
                    icon: "pi-play-circle",
                    isAllowed: true,
                    additionalCheckNotAllowedMessage: (data) => {
                      // For GoogleForm, check if content (URL) exists instead of contentType
                      if (
                        data?.type === "GoogleForm" ||
                        data?.type === "googleform"
                      ) {
                        return data?.content
                          ? "no_permission"
                          : "no_content_available";
                      }
                      return data.contentType
                        ? "no_permission"
                        : "no_content_available";
                    },
                    additionalCheckForAllowed: (data) => {
                      // For GoogleForm, check if content (URL) exists instead of contentType
                      if (
                        data?.type === "GoogleForm" ||
                        data?.type === "googleform"
                      ) {
                        return data?.content;
                      }
                      return data?.contentType;
                    },
                    action: (data) => {
                      const type = data?.type;

                      const payload = {
                        learningObjectId: data.id,
                        domain: session.current.enterprise?.subDomain,
                        accessToken: session.current.token,
                      };

                      if (type === "Scorm") {
                        const version = !data?.contentType.includes("1.2")
                          ? "SCORM_2004"
                          : "SCORM_1.2";
                        setPreviewUrl(
                          `scorm/preview.html?launchUrl=${data?.launchPath}&version=${version}`
                        );
                      } else if (type === "Pdf") {
                        setPreviewUrl(
                          `pdf/preview.html?launchUrl=${data?.launchPath}`
                        );
                      } else if (type === "Video") {
                        setPreviewUrl(
                          `video/preview.html?launchUrl=${data?.launchPath}`
                        );
                      } else if (type === "HTML") {
                        setPreviewUrl(
                          `html/preview.html?details=${btoa(
                            JSON.stringify(payload)
                          )}`
                        );
                      } else if (
                        type === "GoogleForm" ||
                        type === "googleform"
                      ) {
                        // For Google Forms, set the URL for preview
                        setPreviewUrl(data?.content);
                      }
                    },
                  },
                ]),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  list: state?.learningObjects?.data?.list || [],
  action: state?.learningObjects?.action,
  requesting: state?.learningObjects?.requesting,
  error: state?.learningObjects?.error,
  query: state?.learningObjects?.query,
  user: state?.self?.user,
  totalCount: state?.learningObjects?.data?.totalCount,
  enterprise: state.global.enterprise,
  token: state.auth.session.accessToken,
});

const mapDispatchToProps = (dispatch) => ({
  listRequest: (data) => dispatch(listRequest(data)),
  setQuery: (data) => dispatch(setQuery(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LearningObjectList);
